import axios from 'axios';
import { signOut } from 'Services/auth.service';
import * as Sentry from '@sentry/react';
import { getApiBaseUrl, getSelectedCountry } from 'utils';
import Utils from 'Shared/Utils';

// Constants
import { HTTP_STATUS_CODES } from '../Views/Common/enum';
import {
  AUTH_PORTAL_URLS,
  FLEX_PATIENT_ACCESS_TOKEN_KEY,
  PAGE_URLS,
  PULSE_PAY_PATIENT_PAYMENT_DOMAIN_PATTERN,
} from 'Routes/Main/constants';
import { isAuthPortalEnabled } from 'utils/auth-portal-url';

const onSuccessResponse = (response) => {
  if (
    response.status === 200 ||
    response.status === 201 ||
    response.status === 202
  ) {
    return {
      type: 'success',
      title: HTTP_STATUS_CODES[response.status],
      message: 'Success',
      data: response?.data || {},
      headers: response?.headers || {},
    };
  } else if (response.status === 204) {
    // sent by s3 upload
    return {
      type: 'success',
      title: HTTP_STATUS_CODES[response.status],
      headers: response?.headers || {},
    };
  } else {
    throw new Error({ response });
  }
};

const onErrorResponse = (error, url = '') => {
  const selectedCountry = getSelectedCountry();
  const isLoginPath = url?.url?.includes('/login');
  const isPylonInitPath = url?.url?.includes('/pylon/init');
  if (!Utils.shouldSkipSentryLogging(error)) {
    Sentry.captureException(error);
  }
  if (error?.response?.status === 400) {
    let msg =
      error.response.data?.message ||
      error.response.data.error ||
      error.response.data?.detail?.[0]?.msg ||
      (typeof error.response.data?.detail === 'string' &&
        error.response.data?.detail) ||
      'Request Failed';

    if (error.response?.data instanceof ArrayBuffer) {
      const decodedString = String.fromCharCode.apply(
        null,
        new Uint8Array(error.response?.data),
      );
      const obj = JSON.parse(decodedString);
      msg = obj?.message || obj?.error;
    }

    return {
      type: 'error',
      title: 'Error',
      message: msg,
      data: null,
    };
  } else if (
    isPylonInitPath &&
    (error?.response?.status === 401 || error?.response?.status === 403)
  ) {
    return {
      type: 'error',
      title: 'Error',
      message: 'Request Failed',
      data: null,
    };
  } else if (
    (error?.response?.status === 401 && !isLoginPath) ||
    error?.response?.status === 403
    // error.code === 'ERR_NETWORK'
    // TODO - 401 response.status not captured by Axios
  ) {
    void (async () => {
      if (
        PULSE_PAY_PATIENT_PAYMENT_DOMAIN_PATTERN.test(
          window.location.hostname,
        )
      ) {
        window.location.reload();

        return;
      }

      await signOut();

      window.zE?.('messenger', 'logoutUser');

      // window?.fcWidget?.user
      //   .clear()
      //   .then(() => {
      //     console.log('Freshchat User cleared');
      //   })
      //   .catch(() => {
      //     console.log('Freshchat User Not cleared');
      //   })
      //   .finally(() => {
      //     window?.fcWidget?.destroy();
      //   });

      localStorage.setItem(`subscribiliToken_${selectedCountry}`, '');
      localStorage.setItem(`userDetail_${selectedCountry}`, '');
      localStorage.setItem(`tokenValidity_${selectedCountry}`, '');
      localStorage.setItem(`refreshToken_${selectedCountry}`, '');

      if (isAuthPortalEnabled()) {
        window.location = AUTH_PORTAL_URLS.LOGIN;
        return;
      }
      window.location = PAGE_URLS.LOGIN;
    })();

    return;
  } else {
    console.log(`Error in URL ${url}`, error);

    return {
      type: 'error',
      title: 'Error',
      message:
        error?.response?.data?.message ||
        error?.response?.data?.detail?.[0]?.msg ||
        (typeof error?.response?.data?.detail === 'string' &&
          error?.response?.data?.detail) ||
        'Request Failed',
      data: null,
    };
  }
};

const onErrorAuthResponse = (error, url = '') => {
  const selectedCountry = getSelectedCountry();
  if (!Utils.shouldSkipSentryLogging(error)) {
    Sentry.captureException(error);
  }
  if (error?.response?.status === 400) {
    let msg =
      error.response.data?.message ||
      error.response.data.error ||
      error.response.data?.detail?.[0]?.msg ||
      (typeof error.response.data?.detail === 'string' &&
        error.response.data?.detail) ||
      'Request Failed';

    if (error.response?.data instanceof ArrayBuffer) {
      const decodedString = String.fromCharCode.apply(
        null,
        new Uint8Array(error.response?.data),
      );
      const obj = JSON.parse(decodedString);
      msg = obj?.message || obj?.error;
    }

    return {
      type: 'error',
      title: 'Error',
      status: error?.response?.status,
      message: msg,
      data: null,
    };
  } else if (
    error?.response?.status === 403
  ) {
    void (async () => {
      if (
        PULSE_PAY_PATIENT_PAYMENT_DOMAIN_PATTERN.test(
          window.location.hostname,
        )
      ) {
        window.location.reload();

        return;
      }

      await signOut();

      window.zE?.('messenger', 'logoutUser');

      localStorage.setItem(`subscribiliToken_${selectedCountry}`, '');
      localStorage.setItem(`userDetail_${selectedCountry}`, '');
      localStorage.setItem(`tokenValidity_${selectedCountry}`, '');
      localStorage.setItem(`refreshToken_${selectedCountry}`, '');

      if (isAuthPortalEnabled()) {
        window.location = AUTH_PORTAL_URLS.LOGIN;
        return;
      }
      window.location = PAGE_URLS.LOGIN;
    })();

    return;
  } else {
    console.log(`Error in URL ${url}`, error);

    return {
      type: 'error',
      title: 'Error',
      status: error?.response?.status,
      message:
        error?.response?.data?.message ||
        error?.response?.data?.detail?.[0]?.msg ||
        (typeof error?.response?.data?.detail === 'string' &&
          error?.response?.data?.detail) ||
        'Request Failed',
      data: null,
    };
  }
};

// Create clients with initial baseURL - will be dynamically updated in interceptors
const client = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL_V2 + '/mgt',
});

const authClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL_V2 + '/appcore/auth',
});

authClient.interceptors.request.use((request) => {
  if (request.url.includes('googleapis.com')) return request;
  if (request.url.includes('s3.amazonaws.com')) return request;
  
  // Update baseURL dynamically based on selected country
  const baseUrl = getApiBaseUrl();
  const fullBaseUrl = baseUrl + '/appcore/auth';
  request.baseURL = fullBaseUrl;
  authClient.defaults.baseURL = fullBaseUrl;
  
  // Get country-specific token
  const selectedCountry = getSelectedCountry();
  const token = localStorage.getItem(`subscribiliToken_${selectedCountry}`) || '';
  
  request.headers.Authorization = `Bearer ${token}`;
  if (request.url.includes(process.env.REACT_APP_WP_URL)) {
    request.headers.Authorization = `Bearer ${token}`;
  } else {
    request.headers.Authorization = `Bearer ${token}`;
    request.headers['x-subscribili-client'] = 'provider-portal';
  }
  return request;
});

client.interceptors.request.use((request) => {
  if (request.url.includes('googleapis.com')) return request;
  if (request.url.includes('s3.amazonaws.com')) return request;

  // Update baseURL dynamically based on selected country
  const baseUrl = getApiBaseUrl();
  const fullBaseUrl = baseUrl + '/mgt';
  request.baseURL = fullBaseUrl;
  client.defaults.baseURL = fullBaseUrl;

  // Get country-specific tokens
  const selectedCountry = getSelectedCountry();
  const subscribiliToken = localStorage.getItem(`subscribiliToken_${selectedCountry}`) || '';

  if (PULSE_PAY_PATIENT_PAYMENT_DOMAIN_PATTERN.test(window.location.hostname)) {
    if (
      request.url.includes(process.env.REACT_APP_FLEX_API_BASE_URL) &&
      (request.url.includes('invoices') ||
        request.url.includes('intents') ||
        request.url.includes('pay'))
    ) {
      // Patient invoice-pay flow: token is invoice-scoped, country-agnostic.
      const flexPatientToken =
        localStorage.getItem(FLEX_PATIENT_ACCESS_TOKEN_KEY) || '';
      request.headers.Authorization = `Bearer ${flexPatientToken}`;
      request.headers['x-subscribili-auth'] = 'invoice';
    }
  } else if (request.url.includes(process.env.REACT_APP_WP_URL)) {
    request.headers.Authorization = `Bearer ${subscribiliToken}`;
  } else {
    request.headers.Authorization = `Bearer ${subscribiliToken}`;
    request.headers['x-subscribili-client'] = 'provider-portal';
  }

  if (request.url.includes('/invoices') && request.url.includes('/download')) {
    request.responseType = 'blob';
  }

  return request;
});

const httpService = (url) =>
  client(url)
    .then(onSuccessResponse)
    .catch((error) => onErrorResponse(error, url));

const httpAuthService = (url) =>
  authClient(url)
    .then(onSuccessResponse)
    .catch((error) => onErrorAuthResponse(error, url));

export default httpService;
export { httpAuthService };
